import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ValidationChangeCard from './ValidationChangeCard';
import { VALIDATION_CHANGES_MODAL } from '../constants';
import type { ValidationChangesModalProps } from '../types';
import useStyle from '../styles';

// No reutiliza `CustomModal` ni `CustomDialog` porque ambos resuelven el cuerpo como texto plano
// y las acciones como botones de texto: aquí el cuerpo es una lista de tarjetas y los dos botones
// tienen jerarquía distinta. `CustomModal` además vive en MUI v5 y esta pantalla es v4.
const ValidationChangesModal = ({
  isOpen,
  changes,
  onAcknowledge,
  onCancel,
  onReview,
}: ValidationChangesModalProps) => {
  const classes = useStyle();

  // Sin cambios no hay nada que alertar: la modal no se abre aunque le pasen `isOpen`.
  if (changes.length === 0) return null;

  // Si el comercio ya existe en Billet la solicitud no puede seguir adelante, asi que la modal
  // deja de ofrecer continuar y pasa a solo confirmar la lectura con un unico boton.
  // Se exige `isAdverse` para no bloquear el caso inverso: dejar de existir en Billet es un
  // cambio a favor y ahi si tiene sentido revisar la solicitud.
  const acknowledgeOnly = changes.some(
    (change) => change.source === 'BILLET' && change.isAdverse,
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      fullWidth
      aria-labelledby="validation-changes-title"
      classes={{ paper: classes.modalPaper }}
    >
      <IconButton
        size="small"
        className={classes.modalClose}
        aria-label={VALIDATION_CHANGES_MODAL.closeLabel}
        onClick={onCancel}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent className={classes.modalContent}>
        <ErrorOutlineIcon className={classes.modalIcon} />

        <Typography id="validation-changes-title" component="h2" className={classes.modalTitle}>
          {VALIDATION_CHANGES_MODAL.title}
        </Typography>

        <Typography className={classes.modalDescription}>
          {VALIDATION_CHANGES_MODAL.description}
        </Typography>

        <Box className={classes.changeList}>
          {changes.map((change) => (
            <ValidationChangeCard key={change.source} change={change} />
          ))}
        </Box>
      </DialogContent>

      <DialogActions disableSpacing className={classes.modalActions}>
        {acknowledgeOnly ? (
          <Button
            variant="outlined"
            className={classes.modalCancelButton}
            onClick={onAcknowledge || onCancel}
          >
            {VALIDATION_CHANGES_MODAL.acknowledgeLabel}
          </Button>
        ) : (
          <>
            <Button variant="outlined" className={classes.modalCancelButton} onClick={onCancel}>
              {VALIDATION_CHANGES_MODAL.cancelLabel}
            </Button>
            <Button variant="contained" className={classes.modalConfirmButton} onClick={onReview}>
              {VALIDATION_CHANGES_MODAL.confirmLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ValidationChangesModal;
